import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { strToU8, zipSync } from "fflate";
import { runPipeline, type PipelineResult } from "../report/pipeline";
import type { LogEvent, SectionProgress } from "../report/types";
import {
  listDependentUsers,
  loginShared,
  getInfo,
  listCenters,
  type DependentUser,
  type CenterInfo,
  type UserInfo,
} from "../api/client";
import { fetchSheetCRows, groupByClient, type SheetClient, type SheetClientEntry } from "../api/sheets";
import {
  listPublishedReports,
  publishReport,
  revokePublishedReport,
  updatePublishedReportExpiry,
  type ManagedPublishedReport,
  type PublishedReport,
} from "../api/publicReports";

interface Props {
  session: { username: string; rememberme: string };
  onLogout: () => void;
}

type Mode = "current" | "sheet" | "assisted" | "manual";
type WorkspaceView = "generate" | "links";
const USER_SEARCH_DEBOUNCE_MS = 450;

interface GeneratedReport {
  data: PipelineResult["data"];
  html: { blob: Blob; url: string; filename: string; content: string };
  rememberme: string;
}

interface BulkTarget {
  rememberme: string;
  centerId: string;
  centerName: string;
}

function generatedReport(out: PipelineResult, rememberme: string): GeneratedReport {
  return {
    data: out.data,
    html: { blob: out.htmlBlob, url: URL.createObjectURL(out.htmlBlob), filename: `${out.filenameBase}.html`, content: out.html },
    rememberme,
  };
}

function userLabel(u: DependentUser): { primary: string; secondary: string } {
  const fullname = [u.name, u.surname].filter(Boolean).join(" ").trim();
  const primary = fullname || u.descr || `Usuario ${u.id}`;
  const secondary = [
    u.descr && fullname ? u.descr : null,
    u.client,
    u.idClient ? `cliente ${u.idClient}` : null,
  ].filter(Boolean).join(" · ");
  return { primary, secondary };
}

function currentYearMonth(): { year: number; month: number } {
  const n = new Date();
  return { year: n.getFullYear(), month: n.getMonth() + 1 };
}

const MONTH_LABELS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Initial state placeholder for progress before pipeline runs.
function initialProgress(): SectionProgress[] {
  return [
    { name: "fetch", title: "Recogida de datos", state: "pending" },
    { name: "summary", title: "Resumen objetivo con IA", state: "pending" },
    { name: "html", title: "Generación HTML", state: "pending" },
  ];
}

export default function Workspace({ session, onLogout }: Props) {
  const [mode, setMode] = useState<Mode>("current");
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>("generate");

  // ---- Current user mode ----
  const [currentCenters, setCurrentCenters] = useState<CenterInfo[]>([]);
  const [currentSelectedCenter, setCurrentSelectedCenter] = useState<CenterInfo | null>(null);
  const [loadingCurrentCenters, setLoadingCurrentCenters] = useState(false);
  const [currentCentersError, setCurrentCentersError] = useState<string | null>(null);

  // Period — default to current year + current month
  const { year: defaultYear, month: defaultMonth } = currentYearMonth();
  const [year, setYear] = useState<number>(defaultYear);
  const [month, setMonth] = useState<number>(defaultMonth);

  // Filters panel collapse state — auto-minimizes once generation starts.
  const [filtersOpen, setFiltersOpen] = useState(true);

  // ---- Sheet mode (Google Sheet "C") ----
  const [sheetClients, setSheetClients] = useState<SheetClient[]>([]);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [sheetPickedClient, setSheetPickedClient] = useState<SheetClient | null>(null);
  const [sheetPickedCenter, setSheetPickedCenter] = useState<SheetClientEntry | null>(null);

  // ---- Manual mode (always blank token) ----
  const [manualRememberme, setManualRememberme] = useState("");
  const [manualCenterId, setManualCenterId] = useState("");

  // ---- Assisted ("Cambiar Usuario") ----
  const [userQuery, setUserQuery] = useState("");
  const [users, setUsers] = useState<DependentUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersTouched, setUsersTouched] = useState(false);

  const [pickedUser, setPickedUser] = useState<DependentUser | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [activeToken, setActiveToken] = useState<string>("");
  const [activeInfo, setActiveInfo] = useState<UserInfo | null>(null);
  const [centers, setCenters] = useState<CenterInfo[]>([]);
  const [selectedCenter, setSelectedCenter] = useState<CenterInfo | null>(null);

  // ---- Pipeline ----
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [progress, setProgress] = useState<SectionProgress[]>(initialProgress());
  const [result, setResult] = useState<GeneratedReport | null>(null);
  const [batchResults, setBatchResults] = useState<GeneratedReport[]>([]);
  const [batchRun, setBatchRun] = useState<{ current: number; total: number; center: string } | null>(null);
  const [paused, setPaused] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const [publication, setPublication] = useState<PublishedReport | null>(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [managedReports, setManagedReports] = useState<ManagedPublishedReport[]>([]);
  const [managedReportsLoading, setManagedReportsLoading] = useState(false);
  const [managedReportsError, setManagedReportsError] = useState<string | null>(null);
  const [linkSearch, setLinkSearch] = useState("");
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [editingLinkExpiry, setEditingLinkExpiry] = useState("");
  const logRef = useRef<HTMLDivElement>(null);
  const runControlRef = useRef({ pauseRequested: false, stopRequested: false });
  const resumeWaiterRef = useRef<(() => void) | null>(null);
  const runAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [logs.length]);

  useEffect(() => {
    if (workspaceView !== "links") return;
    let cancelled = false;
    setManagedReportsLoading(true);
    setManagedReportsError(null);
    listPublishedReports(session.rememberme)
      .then(({ reports }) => { if (!cancelled) setManagedReports(reports); })
      .catch((error) => { if (!cancelled) setManagedReportsError((error as Error).message || "No se han podido cargar los enlaces."); })
      .finally(() => { if (!cancelled) setManagedReportsLoading(false); });
    return () => { cancelled = true; };
  }, [workspaceView, session.rememberme]);

  useEffect(() => {
    if (mode !== "current") return;
    let aborted = false;
    setLoadingCurrentCenters(true);
    setCurrentCentersError(null);
    listCenters(session.rememberme)
      .then((cs) => { if (!aborted) setCurrentCenters(cs); })
      .catch((err: any) => {
        if (aborted) return;
        const msg = err?.response?.status
          ? `HTTP ${err.response.status} ${err.message || ""}`.trim()
          : err?.message || String(err);
        setCurrentCentersError(msg);
      })
      .finally(() => { if (!aborted) setLoadingCurrentCenters(false); });
    return () => { aborted = true; };
  }, [mode, session.rememberme]);

  const [sheetFetched, setSheetFetched] = useState(false);
  const sheetFetchInFlight = useRef(false);
  useEffect(() => {
    if (mode !== "sheet") return;
    if (sheetFetched || sheetFetchInFlight.current) return;
    sheetFetchInFlight.current = true;
    setSheetLoading(true);
    setSheetError(null);
    fetchSheetCRows()
      .then((rows) => setSheetClients(groupByClient(rows)))
      .catch((err: any) => setSheetError(err?.message || String(err)))
      .finally(() => {
        setSheetLoading(false);
        setSheetFetched(true);
        sheetFetchInFlight.current = false;
      });
  }, [mode, sheetFetched]);

  useEffect(() => {
    if (mode !== "assisted") return;
    if (pickedUser) return;
    const query = userQuery.trim();
    if (!query) {
      setUsers([]);
      setUsersError(null);
      setUsersTouched(false);
      setLoadingUsers(false);
      return;
    }
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      setLoadingUsers(true);
      setUsersError(null);
      setUsersTouched(true);
      try {
        const page = await listDependentUsers(session.rememberme, {
          filter: query,
          rows: 50, start: 0,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setUsers(page.rows);
      } catch (err: any) {
        if (controller.signal.aborted || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        const msg = err?.response?.status
          ? `HTTP ${err.response.status} ${err.message || ""}`.trim()
          : err?.message || String(err);
        setUsersError(msg);
      } finally {
        if (!controller.signal.aborted) setLoadingUsers(false);
      }
    }, USER_SEARCH_DEBOUNCE_MS);
    return () => { clearTimeout(handle); controller.abort(); };
  }, [mode, userQuery, session.rememberme, pickedUser]);

  async function selectUser(u: DependentUser) {
    setPickedUser(u);
    setSwitchError(null);
    setActiveToken(""); setActiveInfo(null);
    setCenters([]); setSelectedCenter(null);
    setSwitching(true);
    try {
      const { activeRememberme } = await loginShared(session.rememberme, u.id);
      setActiveToken(activeRememberme);
      const [info, centerList] = await Promise.all([
        getInfo(activeRememberme),
        listCenters(activeRememberme),
      ]);
      setActiveInfo(info);
      setCenters(centerList);
    } catch (err: any) {
      const msg = err?.response?.status
        ? `HTTP ${err.response.status} ${err.message || ""}`.trim()
        : err?.message || String(err);
      setSwitchError(msg);
      setPickedUser(null);
    } finally {
      setSwitching(false);
    }
  }

  function clearUserSelection() {
    setPickedUser(null);
    setSwitching(false);
    setSwitchError(null);
    setActiveToken("");
    setActiveInfo(null);
    setCenters([]);
    setSelectedCenter(null);
    setUserQuery("");
    setUsers([]);
    setUsersError(null);
    setUsersTouched(false);
    resetReport();
  }

  function pickCenter(c: CenterInfo) {
    setSelectedCenter(c);
    resetReport();
  }

  // Reset only the cost-center step (and downstream). Keeps the picked user.
  function clearCenterSelection() {
    setSelectedCenter(null);
    resetReport();
  }

  const resolved = useMemo(() => {
    const periodOk = Number.isInteger(year) && year >= 2000 && month >= 1 && month <= 12;
    if (mode === "manual") {
      return {
        rememberme: manualRememberme.trim(),
        centerId: manualCenterId.trim(),
        centerName: undefined as string | undefined,
        valid: !!manualRememberme.trim() && !!manualCenterId.trim() && periodOk,
      };
    }
    if (mode === "current") {
      return {
        rememberme: session.rememberme,
        centerId: currentSelectedCenter?.id || "",
        centerName: currentSelectedCenter?.descr,
        valid: !!currentSelectedCenter && periodOk,
      };
    }
    if (mode === "sheet") {
      return {
        rememberme: sheetPickedCenter?.rememberme || "",
        centerId: sheetPickedCenter?.centerId || "",
        centerName: sheetPickedCenter?.centerName,
        valid: !!sheetPickedCenter && periodOk,
      };
    }
    return {
      rememberme: activeToken,
      centerId: selectedCenter?.id || "",
      centerName: selectedCenter?.descr,
      valid: !!activeToken && !!selectedCenter && periodOk,
    };
  }, [mode, manualRememberme, manualCenterId, activeToken, selectedCenter, currentSelectedCenter, sheetPickedCenter, session.rememberme, year, month]);

  const activeStatus = useMemo(() => {
    const active = progress.find((p) => p.state === "active");
    if (active) return `Procesando: ${active.title}`;
    if (running) return "Inicializando…";
    return null;
  }, [progress, running]);

  const dataCollection = progress[0];
  const dataCollectionPct = dataCollection?.state === "done" ? 100 : dataCollection?.state === "active" ? 62 : 0;
  const generatedSections = progress.slice(1).filter((p) => p.state !== "pending");

  const bulkTargets = useMemo<BulkTarget[]>(() => {
    if (mode === "current") return currentCenters.map((center) => ({
      rememberme: session.rememberme, centerId: center.id, centerName: center.descr,
    }));
    if (mode === "assisted") return centers.map((center) => ({
      rememberme: activeToken, centerId: center.id, centerName: center.descr,
    }));
    if (mode === "sheet" && sheetPickedClient) return sheetPickedClient.centers.map((center) => ({
      rememberme: center.rememberme, centerId: center.centerId, centerName: center.centerName,
    }));
    return [];
  }, [mode, currentCenters, session.rememberme, centers, activeToken, sheetPickedClient]);
  const canGenerateAll = bulkTargets.length > 0 && Number.isInteger(year) && year >= 2000 && month >= 1 && month <= 12;
  const bulkLabel = mode === "sheet" && sheetPickedClient
    ? `Generar los ${bulkTargets.length} informes de ${sheetPickedClient.name}`
    : `Generar los ${bulkTargets.length} informes de la cuenta`;

  function clearResult() {
    const urls = new Set([result?.html.url, ...batchResults.map((item) => item.html.url)].filter(Boolean));
    urls.forEach((url) => URL.revokeObjectURL(url!));
    setResult(null);
    setBatchResults([]);
    setBatchRun(null);
    setPublication(null);
    setExpiryDate("");
    setShareError(null);
  }

  function resetReport() {
    setLogs([]);
    clearResult();
    setProgress(initialProgress());
  }

  async function currentSheetEntry(entry: SheetClientEntry): Promise<SheetClientEntry> {
    const freshEntries = await fetchSheetCRows();
    setSheetClients(groupByClient(freshEntries));
    const fresh = freshEntries.find((candidate) =>
      candidate.centerId === entry.centerId
      && candidate.clientName === entry.clientName
      && candidate.centerName === entry.centerName,
    );
    if (!fresh) return entry;
    setSheetPickedClient((client) => client?.name === fresh.clientName
      ? { name: fresh.clientName, centers: freshEntries.filter((candidate) => candidate.clientName === fresh.clientName) }
      : client);
    setSheetPickedCenter(fresh);
    return fresh;
  }

  async function onGenerate() {
    if (!resolved.valid || running) return;
    setRunning(true);
    setFiltersOpen(false);
    setLogs([]);
    runControlRef.current = { pauseRequested: false, stopRequested: false };
    setPaused(false); setStopRequested(false);
    clearResult();
    setProgress(initialProgress());
    const controller = new AbortController();
    runAbortRef.current = controller;
    try {
      let rememberme = resolved.rememberme;
      let centerId = resolved.centerId;
      let centerName = resolved.centerName;
      if (mode === "sheet" && sheetPickedCenter) {
        setLogs((previous) => [...previous, { level: "step", ts: new Date().toLocaleTimeString("es-ES", { hour12: false }), message: "Actualizando acceso del cliente desde Google Sheets…" }]);
        try {
          const current = await currentSheetEntry(sheetPickedCenter);
          rememberme = current.rememberme;
          centerId = current.centerId;
          centerName = current.centerName;
        } catch (error) {
          setLogs((previous) => [...previous, { level: "err", ts: new Date().toLocaleTimeString("es-ES", { hour12: false }), message: `No se ha podido actualizar la hoja; se usará el acceso cargado: ${(error as Error).message}` }]);
        }
      }
      const out: PipelineResult = await runPipeline({
        rememberme,
        centerId,
        centerName,
        year, month,
        signal: controller.signal,
        onLog: (e) => setLogs((prev) => [...prev, e]),
        onProgress: (p) => setProgress(p),
      });
      if (!runControlRef.current.stopRequested) setResult(generatedReport(out, rememberme));
      else setLogs((previous) => [...previous, { level: "info", ts: new Date().toLocaleTimeString("es-ES", { hour12: false }), message: "Proceso detenido antes de publicar el informe." }]);
    } catch { /* logged */ }
    finally { runAbortRef.current = null; setPaused(false); setStopRequested(false); setRunning(false); }
  }

  async function onGenerateAll() {
    if (!canGenerateAll || running) return;
    setRunning(true);
    setFiltersOpen(false);
    setLogs([]);
    runControlRef.current = { pauseRequested: false, stopRequested: false };
    setPaused(false); setStopRequested(false);
    clearResult();
    setProgress(initialProgress());
    let targets = bulkTargets;
    try {
      if (mode === "sheet" && sheetPickedClient) {
        setLogs((previous) => [...previous, { level: "step", ts: new Date().toLocaleTimeString("es-ES", { hour12: false }), message: "Actualizando accesos de clientes desde Google Sheets…" }]);
        const freshEntries = await fetchSheetCRows();
        setSheetClients(groupByClient(freshEntries));
        targets = sheetPickedClient.centers.map((entry) => {
          const current = freshEntries.find((candidate) => candidate.centerId === entry.centerId && candidate.clientName === entry.clientName && candidate.centerName === entry.centerName);
          return current ? { rememberme: current.rememberme, centerId: current.centerId, centerName: current.centerName } : { rememberme: entry.rememberme, centerId: entry.centerId, centerName: entry.centerName };
        });
      }
    } catch (error) {
      setLogs((previous) => [...previous, { level: "err", ts: new Date().toLocaleTimeString("es-ES", { hour12: false }), message: `No se ha podido actualizar la hoja; se usará el acceso cargado: ${(error as Error).message}` }]);
    }
    const outputs: GeneratedReport[] = [];
    for (let index = 0; index < targets.length; index += 1) {
      if (!(await waitForBatchResume())) break;
      const target = targets[index];
      setBatchRun({ current: index + 1, total: targets.length, center: target.centerName });
      const controller = new AbortController();
      runAbortRef.current = controller;
      try {
        const out = await runPipeline({
          rememberme: target.rememberme,
          centerId: target.centerId,
          centerName: target.centerName,
          year, month,
          signal: controller.signal,
          onLog: (event) => setLogs((previous) => [...previous, { ...event, message: `[${target.centerName}] ${event.message}` }]),
          onProgress: setProgress,
        });
        if (runControlRef.current.stopRequested) break;
        const generated = generatedReport(out, target.rememberme);
        outputs.push(generated);
        setBatchResults([...outputs]);
        if (outputs.length === 1) setResult(generated);
      } catch {
        // runPipeline already emits the precise error in the activity log.
      }
      finally { runAbortRef.current = null; }
    }
    setBatchRun(null);
    setPaused(false); setStopRequested(false);
    setRunning(false);
  }

  async function waitForBatchResume(): Promise<boolean> {
    if (runControlRef.current.stopRequested) return false;
    if (!runControlRef.current.pauseRequested) return true;
    setPaused(true);
    await new Promise<void>((resolve) => { resumeWaiterRef.current = resolve; });
    resumeWaiterRef.current = null;
    return !runControlRef.current.stopRequested;
  }
  function pauseGeneration() {
    runControlRef.current.pauseRequested = true;
    setPaused(true);
    setLogs((previous) => [...previous, { level: "info", ts: new Date().toLocaleTimeString("es-ES", { hour12: false }), message: "Pausa solicitada: se detendrá al terminar el informe actual." }]);
  }
  function resumeGeneration() {
    runControlRef.current.pauseRequested = false;
    setPaused(false);
    resumeWaiterRef.current?.();
    setLogs((previous) => [...previous, { level: "info", ts: new Date().toLocaleTimeString("es-ES", { hour12: false }), message: "Proceso reanudado." }]);
  }
  function stopGeneration() {
    runControlRef.current.stopRequested = true;
    runControlRef.current.pauseRequested = false;
    setStopRequested(true); setPaused(false);
    resumeWaiterRef.current?.();
    runAbortRef.current?.abort();
    setLogs((previous) => [...previous, { level: "info", ts: new Date().toLocaleTimeString("es-ES", { hour12: false }), message: "Detención solicitada: se cancelará al terminar la operación en curso." }]);
  }

  function downloadHtml() {
    if (!result) return;
    const link = document.createElement("a");
    link.href = result.html.url;
    link.download = result.html.filename;
    link.click();
  }
  function openHtml() { if (result) window.open(result.html.url, "_blank"); }
  function printPdf() {
    if (!result) return;
    const target = window.open(result.html.url, "_blank");
    target?.addEventListener("load", () => target.print(), { once: true });
  }
  function downloadAllHtml() {
    if (!batchResults.length) return;
    const files = Object.fromEntries(batchResults.map((item) => [item.html.filename, strToU8(item.html.content)]));
    const zip = new Blob([zipSync(files, { level: 6 })], { type: "application/zip" });
    const url = URL.createObjectURL(zip);
    const link = document.createElement("a");
    link.href = url;
    link.download = `informes-pack-ia-${year}-${String(month).padStart(2, "0")}.zip`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  async function shareCurrentReport() {
    if (!result || sharing) return;
    setSharing(true); setShareError(null);
    try {
      const published = await publishReport({
        // A report can be generated from a Sheet or a delegated user. Validate
        // the exact session that was used to collect its data, not the operator
        // session that happens to be open in this browser.
        rememberme: result.rememberme,
        report: result.data,
        brandLogoUri: `${window.location.origin}/logo_pack_ia.svg`,
        center: result.data.center,
        period: result.data.month_label,
        filename: result.html.filename,
      });
      setPublication(published);
      setExpiryDate(published.expiresAt.slice(0, 10));
      void refreshManagedReports();
      await navigator.clipboard?.writeText(published.url);
    } catch (error) {
      setShareError((error as Error).message || "No se ha podido crear el enlace");
    } finally { setSharing(false); }
  }
  async function revokeCurrentPublication() {
    if (!publication || sharing) return;
    setSharing(true); setShareError(null);
    try {
      await revokePublishedReport(session.rememberme, publication.id);
      setManagedReports((reports) => reports.map((report) => report.id === publication.id ? { ...report, revoked: true } : report));
      setPublication(null);
      setExpiryDate("");
    } catch (error) {
      setShareError((error as Error).message || "No se ha podido revocar el enlace");
    } finally { setSharing(false); }
  }
  async function updatePublicationExpiry() {
    if (!publication || sharing || !expiryDate) return;
    setSharing(true); setShareError(null);
    try {
      const updated = await updatePublishedReportExpiry(
        session.rememberme,
        publication.id,
        new Date(`${expiryDate}T23:59:59.999`).toISOString(),
      );
      setPublication({ ...publication, expiresAt: updated.expiresAt });
      setExpiryDate(updated.expiresAt.slice(0, 10));
      setManagedReports((reports) => reports.map((report) => report.id === publication.id ? { ...report, expiresAt: updated.expiresAt } : report));
    } catch (error) {
      setShareError((error as Error).message || "No se ha podido actualizar la fecha límite");
    } finally { setSharing(false); }
  }
  async function copyPublicationLink() {
    if (!publication) return;
    try {
      await navigator.clipboard.writeText(publication.url);
      setShareError(null);
    } catch {
      setShareError("No se ha podido copiar el enlace. Puedes abrirlo y copiarlo desde el navegador.");
    }
  }
  async function refreshManagedReports() {
    setManagedReportsLoading(true);
    setManagedReportsError(null);
    try {
      const { reports } = await listPublishedReports(session.rememberme);
      setManagedReports(reports);
    } catch (error) {
      setManagedReportsError((error as Error).message || "No se han podido cargar los enlaces.");
    } finally {
      setManagedReportsLoading(false);
    }
  }
  async function copyManagedLink(report: ManagedPublishedReport) {
    if (!report.url) return;
    try {
      await navigator.clipboard.writeText(report.url);
    } catch {
      setManagedReportsError("No se ha podido copiar el enlace. Ábrelo y cópialo desde el navegador.");
    }
  }
  async function revokeManagedLink(report: ManagedPublishedReport) {
    if (report.revoked || sharing) return;
    setSharing(true);
    setManagedReportsError(null);
    try {
      await revokePublishedReport(session.rememberme, report.id);
      setManagedReports((reports) => reports.map((item) => item.id === report.id ? { ...item, revoked: true } : item));
      if (publication?.id === report.id) setPublication(null);
    } catch (error) {
      setManagedReportsError((error as Error).message || "No se ha podido revocar el enlace.");
    } finally { setSharing(false); }
  }
  async function saveManagedLinkExpiry(report: ManagedPublishedReport) {
    if (!editingLinkExpiry || sharing) return;
    setSharing(true);
    setManagedReportsError(null);
    try {
      const updated = await updatePublishedReportExpiry(
        session.rememberme, report.id, new Date(`${editingLinkExpiry}T23:59:59.999`).toISOString(),
      );
      setManagedReports((reports) => reports.map((item) => item.id === report.id ? { ...item, expiresAt: updated.expiresAt } : item));
      if (publication?.id === report.id) {
        setPublication({ ...publication, expiresAt: updated.expiresAt });
        setExpiryDate(updated.expiresAt.slice(0, 10));
      }
      setEditingLinkId(null);
    } catch (error) {
      setManagedReportsError((error as Error).message || "No se ha podido actualizar la fecha límite.");
    } finally { setSharing(false); }
  }
  function goHome() {
    if (running) return;
    resetReport();
    setWorkspaceView("generate");
    setFiltersOpen(true);
    setMode("current");
    setCurrentSelectedCenter(null);
    setSheetPickedClient(null);
    setSheetPickedCenter(null);
    setPickedUser(null);
    setSelectedCenter(null);
    setActiveToken("");
    setActiveInfo(null);
    setManualRememberme("");
    setManualCenterId("");
    setUserQuery("");
    setEditingLinkId(null);
  }
  function formatDate(value: string | null): string {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date.toLocaleDateString("es-ES") : "—";
  }
  function reportStatus(report: ManagedPublishedReport): "Activo" | "Caducado" | "Revocado" {
    if (report.revoked) return "Revocado";
    return new Date(report.expiresAt).getTime() <= Date.now() ? "Caducado" : "Activo";
  }
  function closeOutputMenu(button: HTMLButtonElement) {
    button.closest("details")?.removeAttribute("open");
  }

  const isProcessing = running || logs.length > 0 || !!result || batchResults.length > 0;
  const filteredManagedReports = useMemo(() => {
    const query = linkSearch.trim().toLocaleLowerCase("es-ES");
    if (!query) return managedReports;
    return managedReports.filter((report) => `${report.center} ${report.period} ${report.filename}`.toLocaleLowerCase("es-ES").includes(query));
  }, [managedReports, linkSearch]);

  return (
    <div className="shell">
      <div className="topbar">
        <div className="topbar-inner">
          <button className="topbar-brand" type="button" onClick={goHome} disabled={running} aria-label="Volver al inicio">
            <img className="topbar-icon" src="/logo_tspoonlab.png" alt="TSpoonLab" />
            <div className="topbar-title">Informe Pack IA</div>
          </button>
          <nav className="topbar-nav" aria-label="Navegación principal">
            <button className={`topbar-nav-button ${workspaceView === "generate" ? "active" : ""}`} type="button" onClick={goHome} disabled={running}>Generar informe</button>
            <button className={`topbar-nav-button ${workspaceView === "links" ? "active" : ""}`} type="button" onClick={() => setWorkspaceView("links")} disabled={running}>Enlaces públicos</button>
          </nav>
          <div className="topbar-account">
            <span style={{ color: "var(--ink3)", fontSize: "0.85rem" }}>{session.username}</span>
            <button className="ghost" onClick={onLogout}>Salir</button>
          </div>
        </div>
      </div>

      <div className={`wide workspace-main ${workspaceView === "generate" && isProcessing ? "is-processing" : ""}`}>
        {workspaceView === "links" ? (
          <section className="links-manager card">
            <div className="links-manager-head">
              <div>
                <span className="filters-title">Enlaces públicos</span>
                <h2 className="title">Gestionar informes compartidos</h2>
                <p className="links-manager-description">Consulta, abre, copia, cambia la fecha límite o revoca los enlaces enviados a clientes.</p>
              </div>
              <button className="action-button action-button--neutral" type="button" onClick={() => void refreshManagedReports()} disabled={managedReportsLoading}>Actualizar</button>
            </div>
            <div className="links-toolbar">
              <input value={linkSearch} onChange={(event) => setLinkSearch(event.target.value)} placeholder="Buscar por centro, período o archivo" aria-label="Buscar enlaces públicos" />
              <span>{managedReports.length} enlace{managedReports.length === 1 ? "" : "s"}</span>
            </div>
            {managedReportsError && <p className="error">{managedReportsError}</p>}
            {managedReportsLoading ? (
              <div className="links-empty"><span className="spinner" /> Cargando enlaces…</div>
            ) : managedReports.length === 0 ? (
              <div className="links-empty">Todavía no se ha creado ningún enlace público.</div>
            ) : (
              <div className="links-table-wrap">
                <table className="links-table">
                  <thead><tr><th>Informe</th><th>Período</th><th>Creado</th><th>Caduca</th><th>Estado</th><th aria-label="Acciones" /></tr></thead>
                  <tbody>
                    {filteredManagedReports.map((report) => {
                      const status = reportStatus(report);
                      const isEditing = editingLinkId === report.id;
                      return <Fragment key={report.id}>
                        <tr>
                          <td data-label="Informe"><strong>{report.center || "Informe Pack IA"}</strong><small>{report.filename || "Informe publicado"}</small></td>
                          <td data-label="Período">{report.period || "—"}</td>
                          <td data-label="Creado">{formatDate(report.createdAt)}</td>
                          <td data-label="Caduca">{formatDate(report.expiresAt)}</td>
                          <td data-label="Estado"><span className={`link-status link-status--${status.toLocaleLowerCase("es-ES")}`}>{status}</span></td>
                          <td className="links-actions" data-label="Acciones">
                            {report.url ? <a className="link-action link-action--open" href={report.url} target="_blank" rel="noreferrer">Abrir</a> : <span className="link-unavailable">Enlace anterior</span>}
                            <button className="link-action link-action--copy" type="button" onClick={() => void copyManagedLink(report)} disabled={!report.url}>Copiar</button>
                            <button className="link-action link-action--edit" type="button" onClick={() => { setEditingLinkId(isEditing ? null : report.id); setEditingLinkExpiry(report.expiresAt.slice(0, 10)); }} disabled={status !== "Activo"}>Fecha</button>
                            <button className="link-action link-action--revoke" type="button" onClick={() => void revokeManagedLink(report)} disabled={status !== "Activo" || sharing}>Revocar</button>
                          </td>
                        </tr>
                        {isEditing && <tr className="link-edit-row"><td colSpan={6}><div><span>El enlace estará disponible hasta:</span><input type="date" value={editingLinkExpiry} min={new Date().toISOString().slice(0, 10)} max={new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10)} onChange={(event) => setEditingLinkExpiry(event.target.value)} /><button className="action-button action-button--blue" type="button" onClick={() => void saveManagedLinkExpiry(report)} disabled={!editingLinkExpiry || sharing}>Guardar fecha</button><button className="action-button action-button--neutral" type="button" onClick={() => setEditingLinkId(null)} disabled={sharing}>Cancelar</button></div></td></tr>}
                      </Fragment>;
                    })}
                    {filteredManagedReports.length === 0 && <tr><td className="links-no-results" colSpan={6}>No hay enlaces que coincidan con la búsqueda.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
            <p className="links-legacy-note">Los enlaces creados antes de esta mejora se pueden revocar o caducar, pero su URL no se puede recuperar por seguridad.</p>
          </section>
        ) : <>
        <div className="form-area">
          <div className={`report-config card ${isProcessing && !filtersOpen ? "is-compact" : ""}`}>
            <div className="filters-header">
              <div>
                <span className="filters-title">Configuración</span>
                {!isProcessing && <h2 className="title">Generar informe</h2>}
              </div>
              {isProcessing && (
              <button className="ghost" type="button"
                onClick={() => setFiltersOpen((v) => !v)}>
                {filtersOpen ? "Minimizar" : "Editar"}
              </button>
              )}
            </div>

          {isProcessing && !filtersOpen && (
            <div className="filter-summary">
              <div className="fs-row">
                <span className="fs-label">Modo</span>
                <span className="fs-value">{mode === "manual" ? "Token manual" : mode === "current" ? "Usuario actual" : mode === "sheet" ? "Clientes (Sheet)" : "Cambiar usuario"}</span>
              </div>
              {mode === "assisted" && pickedUser && (
                <div className="fs-row">
                  <span className="fs-label">Usuario</span>
                  <span className="fs-value">{userLabel(pickedUser).primary}</span>
                </div>
              )}
              <div className="fs-row">
                <span className="fs-label">Centro</span>
                <span className="fs-value">
                  {mode === "manual" ? (manualCenterId || "—")
                    : mode === "current" ? (currentSelectedCenter?.descr || "—")
                    : mode === "sheet" ? (sheetPickedCenter?.centerName || "—")
                    : (selectedCenter?.descr || "—")}
                </span>
              </div>
              <div className="fs-row">
                <span className="fs-label">Periodo</span>
                <span className="fs-value">{MONTH_LABELS[month - 1]} {year}</span>
              </div>
            </div>
          )}

          <div className={`filters-body ${isProcessing && !filtersOpen ? "is-collapsed" : ""}`}>
          <div className="tabs">
            <button className={`tab ${mode === "current" ? "active" : ""}`}
              onClick={() => setMode("current")} disabled={running}>Usuario actual</button>
            <button className={`tab ${mode === "sheet" ? "active" : ""}`}
              onClick={() => setMode("sheet")} disabled={running}>Clientes (Sheet)</button>
            <button className={`tab ${mode === "assisted" ? "active" : ""}`}
              onClick={() => setMode("assisted")} disabled={running}>Cambiar usuario</button>
            <button className={`tab ${mode === "manual" ? "active" : ""}`}
              onClick={() => setMode("manual")} disabled={running}>Token manual</button>
          </div>

          {mode === "sheet" && (
            <div className="mode-panel">
              {sheetPickedCenter ? (
                <div className="picked-stack">
                  <div className="picked-row">
                    <span className="picked-label">Cliente</span>
                    <div className="picked-body">
                      <div className="pl-primary">{sheetPickedClient?.name}</div>
                    </div>
                    <button className="ghost"
                      onClick={() => { setSheetPickedCenter(null); resetReport(); }}
                      disabled={running}>Cambiar</button>
                  </div>
                  <div className="picked-row">
                    <span className="picked-label">Centro</span>
                    <div className="picked-body">
                      <div className="pl-primary">{sheetPickedCenter.centerName}</div>
                      <div className="pl-secondary">ID {sheetPickedCenter.centerId} · hoja {sheetPickedCenter.sheetName}</div>
                    </div>
                    <button className="ghost"
                      onClick={() => { setSheetPickedCenter(null); resetReport(); }}
                      disabled={running}>Cambiar</button>
                  </div>
                </div>
              ) : sheetPickedClient ? (
                <>
                  <div className="picked-stack">
                    <div className="picked-row">
                      <span className="picked-label">Cliente</span>
                      <div className="picked-body">
                        <div className="pl-primary">{sheetPickedClient.name}</div>
                        <div className="pl-secondary">{sheetPickedClient.centers.length} centros</div>
                      </div>
                      <button className="ghost"
                        onClick={() => { setSheetPickedClient(null); resetReport(); }}
                        disabled={running}>Cambiar</button>
                    </div>
                  </div>
                  <div className="step-card">
                    <label>Centro de coste</label>
                    {sheetPickedClient.centers.length === 0 ? (
                      <p className="hint">Este cliente no tiene centros en la hoja.</p>
                    ) : (
                      <ul className="picklist">
                        {sheetPickedClient.centers.map((c) => (
                          <li key={`${c.centerId}-${c.sheetName}`}
                            onClick={() => { setSheetPickedCenter(c); resetReport(); }}>
                            <div>
                              <div className="pl-primary">{c.centerName}</div>
                              <div className="pl-secondary">ID {c.centerId} · hoja {c.sheetName}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              ) : (
                <div className="step-card">
                  {sheetLoading && (
                    <div className="status"><span className="spinner" /> <span>Cargando hoja C…</span></div>
                  )}
                  {sheetError && (
                    <>
                      <p className="error">Error: {sheetError}</p>
                      <button className="ghost" type="button"
                        onClick={() => { setSheetFetched(false); setSheetError(null); }}>
                        Reintentar
                      </button>
                    </>
                  )}
                  {!sheetLoading && !sheetError && (
                    <>
                      <div className="selection-card-head">
                        <label>Cliente</label>
                        <button className="ghost selection-refresh" type="button" onClick={() => { setSheetFetched(false); setSheetError(null); }}>
                          Actualizar datos
                        </button>
                      </div>
                      {sheetClients.length === 0 ? (
                        <p className="hint">No hay clientes en la hoja C.</p>
                      ) : (
                        <ul className="picklist">
                          {sheetClients.map((c) => (
                            <li key={c.name} onClick={() => { setSheetPickedClient(c); resetReport(); }}>
                              <div>
                                <div className="pl-primary">{c.name}</div>
                                <div className="pl-secondary">{c.centers.length} centros</div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {mode === "current" && (
            <div className="mode-panel">
              {currentSelectedCenter ? (
                <div className="picked-stack">
                  <div className="picked-row">
                    <span className="picked-label">Centro</span>
                    <div className="picked-body">
                      <div className="pl-primary">{currentSelectedCenter.descr}</div>
                      <div className="pl-secondary">ID {currentSelectedCenter.id} · {currentSelectedCenter.currency}</div>
                    </div>
                    <button className="ghost"
                      onClick={() => { setCurrentSelectedCenter(null); resetReport(); }}
                      disabled={running}>Cambiar</button>
                  </div>
                </div>
              ) : (
                <div className="step-card">
                  {loadingCurrentCenters && (
                    <div className="status"><span className="spinner" /> <span>Cargando centros…</span></div>
                  )}
                  {currentCentersError && <p className="error">Error: {currentCentersError}</p>}
                  {!loadingCurrentCenters && !currentCentersError && (
                    <>
                      <label>Centro de coste</label>
                      {currentCenters.length === 0 ? (
                        <p className="hint">El usuario actual no tiene centros accesibles.</p>
                      ) : (
                        <ul className="picklist">
                          {currentCenters.map((c) => (
                            <li key={c.id} onClick={() => { setCurrentSelectedCenter(c); resetReport(); }}>
                              <div>
                                <div className="pl-primary">{c.descr}</div>
                                <div className="pl-secondary">ID {c.id} · {c.currency}</div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {mode === "manual" && (
            <div className="mode-panel">
              <div className="field">
                <label>Token RememberMe</label>
                <input value={manualRememberme} onChange={(e) => setManualRememberme(e.target.value)}
                  placeholder="Token de acceso TSpoonLab" disabled={running} autoComplete="off" />
              </div>
              <div className="field">
                <label>Centro de coste (ID)</label>
                <input value={manualCenterId} onChange={(e) => setManualCenterId(e.target.value)}
                  placeholder="ID del centro de coste" disabled={running} />
              </div>
            </div>
          )}

          {mode === "assisted" && !pickedUser && (
            <div className="mode-panel">
              <div className="field">
                <label>Buscar usuario</label>
                <div className="search-input">
                  <input value={userQuery} onChange={(e) => setUserQuery(e.target.value)}
                    placeholder="Nombre, apellido o descripción" disabled={running} autoFocus />
                  {loadingUsers && <span className="spinner spinner-inline" />}
                  {!loadingUsers && userQuery && (
                    <button className="search-clear" type="button"
                      onClick={() => setUserQuery("")} aria-label="Limpiar">×</button>
                  )}
                </div>
              </div>
              <div className="list">
                {usersError && <p className="error">Error: {usersError}</p>}
                {!usersError && !userQuery.trim() && (
                  <p className="hint">Escribe para buscar usuarios. No se cargan resultados hasta que haya texto.</p>
                )}
                {!usersError && usersTouched && !loadingUsers && users.length === 0 && (
                  <p className="hint">Sin resultados.</p>
                )}
                {users.length > 0 && (
                  <ul className={`picklist ${loadingUsers ? "is-loading" : ""}`}>
                    {users.map((u) => {
                      const { primary, secondary } = userLabel(u);
                      return (
                        <li key={u.id} onClick={() => selectUser(u)}>
                          <div>
                            <div className="pl-primary">{primary}</div>
                            {secondary && <div className="pl-secondary">{secondary}</div>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          {mode === "assisted" && pickedUser && (
            <>
              {/* Compact picked stack — user (and center once chosen) */}
              <div className="picked-stack">
                <div className="picked-row">
                  <span className="picked-label">Usuario</span>
                  <div className="picked-body">
                    <div className="pl-primary">{userLabel(pickedUser).primary}</div>
                    <div className="pl-secondary">{userLabel(pickedUser).secondary || `ID ${pickedUser.id}`}</div>
                  </div>
                  <button className="ghost" onClick={clearUserSelection} disabled={running || switching}>
                    Cambiar
                  </button>
                </div>

                {selectedCenter && (
                  <div className="picked-row">
                    <span className="picked-label">Centro</span>
                    <div className="picked-body">
                      <div className="pl-primary">{selectedCenter.descr}</div>
                      <div className="pl-secondary">ID {selectedCenter.id} · {selectedCenter.currency}</div>
                    </div>
                    <button className="ghost" onClick={clearCenterSelection} disabled={running}>
                      Cambiar
                    </button>
                  </div>
                )}
              </div>

              {/* Step: pick a center (only while no center is selected yet) */}
              {!selectedCenter && (
                <div className="step-card">
                  {switching && (
                    <div className="status">
                      <span className="spinner" /> <span>Cambiando a usuario y cargando centros…</span>
                    </div>
                  )}
                  {switchError && <p className="error">Error: {switchError}</p>}
                  {!switching && !switchError && activeToken && (
                    <>
                      <label>Centro de coste</label>
                      {centers.length === 0 ? (
                        <p className="hint">Este usuario no tiene centros accesibles.</p>
                      ) : (
                        <ul className="picklist">
                          {centers.map((c) => (
                            <li key={c.id} onClick={() => pickCenter(c)}>
                              <div>
                                <div className="pl-primary">{c.descr}</div>
                                <div className="pl-secondary">ID {c.id} · {c.currency}</div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* The period belongs to a selected center or to a whole-account batch. */}
          {(mode === "manual"
            || (mode === "current" && (currentSelectedCenter || currentCenters.length > 0))
            || (mode === "sheet" && (sheetPickedCenter || sheetPickedClient))
            || (mode === "assisted" && pickedUser && (selectedCenter || centers.length > 0))) && (
            <div className="step-card period-card">
              <label>Periodo</label>
              <div className="period-row">
                <div className="period-field">
                  <span className="period-sublabel">Año</span>
                  <input type="number" value={year}
                    onChange={(e) => { setYear(Number(e.target.value)); resetReport(); }}
                    min={2020} max={2100} disabled={running} />
                </div>
                <div className="period-field">
                  <span className="period-sublabel">Mes</span>
                  <select value={month}
                    onChange={(e) => { setMonth(Number(e.target.value)); resetReport(); }}
                    disabled={running}>
                    {MONTH_LABELS.map((label, i) => (
                      <option key={i + 1} value={i + 1}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          </div>{/* /filters-body */}

          {(!isProcessing || filtersOpen) && (
            <div className="generate-actions">
              <button className="primary" onClick={onGenerate} disabled={!resolved.valid || running}>
                {running ? "Generando…" : (result ? "Volver a generar" : "Generar informe Pack IA")}
              </button>
              {(mode === "current" || mode === "assisted" || (mode === "sheet" && sheetPickedClient)) && (
                <button className="ghost bulk-generate" onClick={onGenerateAll} disabled={!canGenerateAll || running}>
                  {bulkLabel}
                </button>
              )}
            </div>
          )}
          </div>
        </div>

        <div className="process-area">
        <div className="progress-side">
          {(running || logs.length > 0) && (
            <div className="card progress-card">
              <div className="progress-topline">
                <div>
                  <span className="filters-title">Progreso</span>
                  <div className="progress-current">{paused ? "Pausado" : stopRequested ? "Deteniendo…" : batchRun ? `Informe ${batchRun.current}/${batchRun.total}: ${batchRun.center}` : activeStatus || (result ? "Informe generado" : "Preparando…")}</div>
                </div>
                {running && <span className="spinner" />}
              </div>
              {running && <div className="progress-controls">
                {paused
                  ? <button className="control-button" onClick={resumeGeneration}>Continuar</button>
                  : <button className="control-button" onClick={pauseGeneration} disabled={stopRequested}>Pausar</button>}
                <button className="control-button control-stop" onClick={stopGeneration} disabled={stopRequested}>Detener</button>
              </div>}
              <div className={`collection-step state-${dataCollection?.state || "pending"}`}>
                <div className="collection-row">
                  <span>Recogida de datos</span>
                  <span>{dataCollection?.state === "done" ? "Completo" : dataCollection?.state === "active" ? "En curso" : "Pendiente"}</span>
                </div>
                <div className="collection-bar"><span style={{ width: `${dataCollectionPct}%` }} /></div>
              </div>
              <ul className="section-list">
                {generatedSections.map((p) => (
                  <li key={p.name}>
                    <span>{p.title}</span>
                    <span className={`state state-${p.state}`}>
                      {p.state === "active" && "En curso"}
                      {p.state === "done" && "Completo"}
                      {p.state === "error" && "Error"}
                    </span>
                  </li>
                ))}
              </ul>
              {logs.length > 0 && (
                <>
                  <div className="log-title">Actividad</div>
                  <div className="log" ref={logRef}>
                    {logs.map((l, i) => (
                      <div key={i} className="line">
                        <span className="ts">{l.ts}</span>
                        <span className={`lv-${l.level}`}>[{l.level.toUpperCase()}]</span> {l.message}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

        </div>{/* /progress-side */}

        <div className="preview-side">
          {result ? (
            <div className="result-stack">
              <div className="result-actions">
                <button className="action-button action-button--green" onClick={resetReport}>Nuevo informe</button>
                <details className="output-menu">
                  <summary>Descargar <span className="output-menu-chevron" aria-hidden="true" /></summary>
                  <div className="output-menu-panel">
                    <button type="button" onClick={(event) => { downloadHtml(); closeOutputMenu(event.currentTarget); }}><strong>HTML</strong><small>Archivo web independiente</small></button>
                    <button type="button" onClick={(event) => { printPdf(); closeOutputMenu(event.currentTarget); }}><strong>PDF</strong><small>Guardar desde el diálogo de impresión</small></button>
                  </div>
                </details>
                <details className="output-menu">
                  <summary>Abrir <span className="output-menu-chevron" aria-hidden="true" /></summary>
                  <div className="output-menu-panel">
                    <button type="button" onClick={(event) => { openHtml(); closeOutputMenu(event.currentTarget); }}><strong>HTML</strong><small>Vista en una pestaña nueva</small></button>
                    <button type="button" onClick={(event) => { printPdf(); closeOutputMenu(event.currentTarget); }}><strong>Vista PDF</strong><small>Abrir para imprimir o guardar</small></button>
                  </div>
                </details>
                <button className="action-button action-button--blue" onClick={shareCurrentReport} disabled={sharing}>{sharing ? "Creando enlace…" : "Compartir enlace"}</button>
              </div>
              {publication && <div className="publication-card">
                <div className="publication-expiry"><span>Enlace público hasta</span><input type="date" value={expiryDate} min={new Date().toISOString().slice(0, 10)} max={new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10)} onChange={(event) => setExpiryDate(event.target.value)} disabled={sharing} /></div>
                <button className="action-button action-button--neutral" onClick={updatePublicationExpiry} disabled={sharing || !expiryDate}>Guardar fecha</button>
                <a className="action-button action-button--blue" href={publication.url} target="_blank" rel="noreferrer">Abrir enlace</a>
                <button className="action-button action-button--danger" onClick={revokeCurrentPublication} disabled={sharing}>Revocar</button>
                <button className="action-button action-button--dark" onClick={copyPublicationLink}>Copiar enlace</button>
              </div>}
              {shareError && <p className="error publication-error">{shareError}</p>}
              {batchResults.length > 1 && <div className="batch-card"><div><strong>{batchResults.length} informes generados</strong><span>Elige uno para previsualizar o descarga todos en HTML.</span></div><button className="ghost" onClick={downloadAllHtml}>Descargar ZIP</button><div className="batch-report-list">{batchResults.map((item) => <button key={item.html.url} className={item === result ? "active" : ""} onClick={() => { setResult(item); setPublication(null); setExpiryDate(""); setShareError(null); }}>{item.data.center}</button>)}</div></div>}
              <iframe className="result-preview html-preview" src={result.html.url} title="Vista previa del informe" />
            </div>
          ) : isProcessing ? (
            <div className="preview-placeholder">
              <span className="spinner" />
              <span>Generando vista previa…</span>
            </div>
          ) : null}
        </div>
        </div>
        </>}
      </div>
    </div>
  );
}
